import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { usd, cardCls, Money } from './shared';

// Books overview tab. Moved out of BooksPage.jsx by LP-309 S0 (unchanged); slice F owns this
// file and turns it into the Finance snapshot dashboard (PLAN 5.6).

function arSource(o) {
  const ar = o.ar;
  if (!ar || !ar.source) return null;
  if (ar.invoices_cents && ar.books_cents) return `From invoices ${usd(ar.invoices_cents)}, from books ${usd(ar.books_cents)}`;
  return ar.source.charAt(0).toUpperCase() + ar.source.slice(1);
}

export default function OverviewTab({ onGo }) {
  const [o, setO] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/books/overview').then(setO).catch((e) => setError(e.message)); }, []);
  if (error) return <p className="text-sm text-rose-600">{error}</p>;
  if (!o) return <p className="text-sm text-[var(--text-secondary)]">Loading...</p>;
  const tile = (label, value, sub, go) => (
    <button key={label} onClick={go} className={`${cardCls} text-left hover:border-primary`}>
      <div className="text-xs text-[var(--text-secondary)]">{label}</div>
      <div className="mt-1 text-lg font-semibold text-[var(--text-primary)]">{value}</div>
      {sub && <div className="mt-1 text-xs text-[var(--text-secondary)]">{sub}</div>}
    </button>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {o.cash.map((c) => tile(c.name, <Money cents={c.balance_cents} />, `as of ${o.as_of}`, () => onGo('register')))}
        {tile('To categorize', `${o.queue_count} ${o.queue_count === 1 ? 'line' : 'lines'}`, o.queue_count ? 'Bank lines waiting for a category' : 'All caught up', () => onGo('queue'))}
        {tile('Last tie-out', o.last_tie_out ? `${o.last_tie_out.period} ${o.last_tie_out.tied ? 'tied' : 'not tied'}` : 'None yet', o.last_tie_out && !o.last_tie_out.tied ? `Off by ${usd(o.last_tie_out.difference_cents)}` : 'Monthly, against the Relay statement', () => onGo('tieout'))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {tile('Owed to CCS (A/R)', <Money cents={o.ar_cents} />, arSource(o), () => onGo('reports'))}
        {tile('Due from Concria Oy', <Money cents={o.due_from_concria_cents} />, 'Re-billed costs', () => onGo('reports'))}
        {tile('CCS owes (A/P)', <Money cents={o.ap_cents} />, null, () => onGo('reports'))}
      </div>
      <div className={cardCls}>
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="font-semibold text-[var(--text-primary)]">This year so far</h2>
          <button onClick={() => onGo('reports')} className="text-xs text-primary">Full P&amp;L</button>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div><dt className="text-xs text-[var(--text-secondary)]">Income</dt><dd><Money cents={o.ytd.income_total_cents} /></dd></div>
          <div><dt className="text-xs text-[var(--text-secondary)]">Cost of goods</dt><dd><Money cents={o.ytd.cogs_total_cents} /></dd></div>
          <div><dt className="text-xs text-[var(--text-secondary)]">Expenses</dt><dd><Money cents={o.ytd.expense_total_cents} /></dd></div>
          <div><dt className="text-xs text-[var(--text-secondary)]">Net income</dt><dd className="font-semibold"><Money cents={o.ytd.net_income_cents} /></dd></div>
        </dl>
        {o.sales_ytd && (
          <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-[var(--border)] pt-3 text-sm sm:grid-cols-4">
            <div><dt className="text-xs text-[var(--text-secondary)]">Sales invoiced</dt><dd><Money cents={o.sales_ytd.invoiced_cents} />{o.sales_ytd.invoiced.source && <span className="block text-xs text-[var(--text-secondary)]">{o.sales_ytd.invoiced.source}</span>}</dd></div>
            <div><dt className="text-xs text-[var(--text-secondary)]">Cash collected</dt><dd><Money cents={o.sales_ytd.collected_cents} />{o.sales_ytd.collected.source && <span className="block text-xs text-[var(--text-secondary)]">{o.sales_ytd.collected.source}</span>}</dd></div>
            <p className="col-span-2 text-xs text-[var(--text-secondary)]">Invoice payments are not posted into the ledger, so the P&amp;L lines above show only what was posted. Sales and collections here include invoices, without counting anything twice.</p>
          </dl>
        )}
        {!o.balanced && <p className="mt-2 text-xs text-rose-600">The balance sheet does not balance. Check the trial balance.</p>}
      </div>
    </div>
  );
}
